import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { listQuotes, QuoteApiError } from '@/lib/quotationService';
import type { CustomerQuoteListItem } from '@/lib/quotationTypes';

const statusBadgeMap: Record<string, { bg: string; text: string; label: string }> = {
  priced: { bg: 'bg-emerald-50', text: 'text-emerald-700', label: '견적 완료' },
  final_offer_requested: { bg: 'bg-amber-50', text: 'text-amber-700', label: '본사 검토중' },
  cancelled: { bg: 'bg-rose-50', text: 'text-rose-700', label: '취소됨' },
  superseded: { bg: 'bg-ink-50', text: 'text-ink-500', label: '대체됨' },
};

const quoteTypeLabelMap: Record<string, string> = {
  production: '양산',
  sample: '샘플',
};

function formatMoney(value: string, currency: string, decimalPlaces: number): string {
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  const formatted = num.toLocaleString(undefined, {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });
  const symbolMap: Record<string, string> = {
    USD: '$',
    KRW: '₩',
    EUR: '€',
    GBP: '£',
    JPY: '¥',
    CNY: '¥',
    SGD: 'S$',
    AED: 'AED ',
  };
  return `${symbolMap[currency] || currency} ${formatted}`;
}

export default function QuotesPage() {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState<CustomerQuoteListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function fetchQuotes() {
      setLoading(true);
      setError(null);
      try {
        const data = await listQuotes(page, pageSize);
        if (cancelled) return;
        setQuotes(data.items);
        setTotal(data.total);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof QuoteApiError) {
          setError(err.message);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('견적 목록을 불러오는 중 오류가 발생했습니다.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchQuotes();
    return () => { cancelled = true; };
  }, [page, pageSize]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-ink-700">{t('dashboard.recentQuotes.title')}</h1>
        <p className="text-sm text-ink-300 mt-1">견적 내역을 확인하고 최종 견적을 요청하세요</p>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <div className="w-8 h-8 border-2 border-ink-200 border-t-ink-500 rounded-full animate-spin" />
          <p className="text-sm text-ink-400">견적 목록을 불러오는 중...</p>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-error-warning-line text-rose-500 text-xl" />
          </div>
          <p className="text-sm text-rose-600 font-medium">{error}</p>
          <button
            onClick={() => setPage((p) => p)}
            className="mt-3 px-4 py-2 text-xs font-bold text-rose-600 border border-rose-300 rounded-lg hover:bg-rose-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            다시 시도
          </button>
        </div>
      )}

      {/* Empty */}
      {!loading && !error && quotes.length === 0 && (
        <div className="bg-white border border-ink-100 rounded-xl p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-cream-100 flex items-center justify-center mx-auto mb-4">
            <i className="ri-file-list-3-line text-cream-400 text-2xl" />
          </div>
          <h3 className="text-base font-bold text-ink-700 mb-1">견적 내역이 없습니다</h3>
          <p className="text-sm text-ink-400 mb-5">새로운 제품에 대한 견적을 요청해보세요</p>
          <button
            onClick={() => navigate('/packaging')}
            className="px-5 py-2.5 text-sm font-bold text-white bg-warm-500 rounded-xl hover:bg-warm-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            제품 둘러보기
          </button>
        </div>
      )}

      {/* List */}
      {!loading && !error && quotes.length > 0 && (
        <div className="space-y-3">
          {quotes.map((q) => {
            const statusMeta = statusBadgeMap[q.status] || { bg: 'bg-ink-50', text: 'text-ink-500', label: q.status };
            return (
              <div
                key={q.quoteId}
                className="bg-white rounded-xl border border-ink-100 p-4 md:p-5 hover:border-warm-200 transition-colors"
              >
                <div className="flex flex-col md:flex-row md:items-start gap-4">
                  {/* Left: info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className="text-xs font-mono text-warm-400">{q.quoteNumber}</span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border ${statusMeta.bg} ${statusMeta.text} border-current/20`}>
                        {statusMeta.label}
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-ink-50 text-ink-500 border border-ink-200">
                        {quoteTypeLabelMap[q.quoteType] || q.quoteType}
                      </span>
                    </div>

                    <div className="flex items-center gap-3 text-xs text-ink-400 flex-wrap">
                      <span>제품 {q.productCount}개</span>
                      <span className="text-ink-200">|</span>
                      <span>{new Date(q.createdAt).toLocaleDateString()}</span>
                      {q.submittedAt && (
                        <>
                          <span className="text-ink-200">|</span>
                          <span>제출: {new Date(q.submittedAt).toLocaleDateString()}</span>
                        </>
                      )}
                    </div>

                    <div className="mt-2 text-sm font-bold text-ink-700">
                      {formatMoney(q.quoteTotal, q.currency, q.currency === 'KRW' ? 0 : 2)}
                    </div>
                  </div>

                  {/* Right: actions */}
                  <div className="flex flex-wrap items-center gap-2 shrink-0">
                    {q.finalOfferAvailable && q.issuedFinalOfferId && (
                      <button
                        onClick={() => navigate(`/my/final-offers/${q.issuedFinalOfferId}`)}
                        className="px-3 py-1.5 text-xs font-bold text-white bg-warm-500 rounded-lg hover:bg-warm-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
                      >
                        <i className="ri-file-text-line" />
                        2차 확정 견적서 보기
                      </button>
                    )}
                    <button
                      onClick={() => navigate(`/my/quotes/${q.quoteId}`)}
                      className="px-3 py-1.5 text-xs font-bold text-warm-500 border border-warm-300 rounded-lg hover:bg-warm-50 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      상세 보기 →
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg border border-ink-200 text-ink-500 hover:bg-cream-50 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors"
              >
                <i className="ri-arrow-left-s-line" />
              </button>
              <span className="text-sm text-ink-600 font-medium">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="w-8 h-8 flex items-center justify-center rounded-lg border border-ink-200 text-ink-500 hover:bg-cream-50 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors"
              >
                <i className="ri-arrow-right-s-line" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}